'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';

import { IoSend } from 'react-icons/io5';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('w-full', 'ea-homepage-newsletter')}>
      <form
        className={cn(
          'flex w-full flex-col items-start justify-start',
          'ea-homepage-newsletter__form',
        )}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-2',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <Input
            aria-label={systemLabels('email')}
            id={`newsletter-email-${seoNewsletterId}`}
            type="email"
            name="email"
            placeholder={t('text183')}
            onChange={(e) => setEmail(e.target.value)}
            className={{
              wrapper: cn(
                'rounded-full border-none bg-white/20 pl-4 pr-1 text-muted-foreground',
                'ea-homepage-newsletter__form-item-email-input',
              ),
            }}
            after={
              <Button
                type="submit"
                aria-label={t('text182')}
                className={cn(
                  'max-w-12 bg-white text-primary hover:bg-white/90 sm:max-w-[400px]',
                  'ea-homepage-newsletter__form-item-email-submit-button',
                )}
                disabled={email.length === 0 || !checked || isSubscribed}
                isLoading={subscribeMutation.isPending}
              >
                <div className="block sm:hidden">
                  <IoSend size={20} />
                </div>
                <p className="hidden sm:block">{t('text182')}</p>
              </Button>
            }
          />
          {isSubscribed && <span className={cn('font-semibold text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-start space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id={`newsletter-gdpr-${seoNewsletterId}`}
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn(
                'text-sm font-normal text-white',
                'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
              )}
            >
              {t('text3587')}{' '}
              <Link prefetch={false} href={ROUTES.GDPR()} className="underline">
                {t('text2342')}
              </Link>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
